// Keep capture and storage in sync with embed/index.js.

const KEY = 'knko_attr'
const WINDOW_MS = 90 * 24 * 60 * 60 * 1000
const GOOGLE_IDS = ['gclid', 'wbraid', 'gbraid']

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY))
  } catch {
    return null
  }
}

const save = value => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value))
  } catch {
    // Ignore blocked storage
  }
}

const clear = () => {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Ignore blocked storage
  }
}

// Keep values as read; the server normalizes tags and click ids are case-sensitive.
export const readTouch = (
  loc = window.location,
  referrer = document.referrer
) => {
  const search = new URLSearchParams(loc.search)
  const hash = new URLSearchParams((loc.hash || '').split('?')[1] || '')
  const get = key => search.get(key) || hash.get(key)

  const googleId = GOOGLE_IDS.map(get).find(Boolean)
  const metaId = get('fbclid')
  const tags = {
    src: get('utm_source'),
    med: get('utm_medium'),
    cmp: get('utm_campaign'),
    cnt: get('utm_content'),
    trm: get('utm_term'),
    cid: googleId || metaId || null,
    cst: googleId ? 'google' : metaId ? 'meta' : null
  }
  if (!Object.values(tags).some(Boolean)) return null

  return {
    ...tags,
    ref: referrer ? referrer.split(/[?#]/)[0] : null,
    lp: (loc.hash || '').startsWith('#/')
      ? loc.hash.slice(1).split('?')[0]
      : loc.pathname,
    ts: new Date().toISOString()
  }
}

// first is written once, last is always replaced.
export const recordTouch = touch => {
  if (!touch) return
  const stored = load() || {}
  save({ v: 1, first: stored.first || touch, last: touch })
}

// Drop last after 90 days; keep first.
export const current = () => {
  const stored = load()
  if (!stored) return null
  const fresh =
    stored.last && Date.now() - Date.parse(stored.last.ts) < WINDOW_MS
  const last = fresh ? stored.last : undefined
  return stored.first || last ? { v: 1, first: stored.first, last } : null
}

// btoa is Latin-1; encode UTF-8 first, then unpadded base64url with nulls dropped.
export const encode = stored => {
  const json = JSON.stringify(stored, (key, value) =>
    value === null ? undefined : value
  )
  const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)))
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

const inIframe = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

// Parent origin is unknown and these messages carry nothing sensitive.
const tellParent = type =>
  window.parent.postMessage(JSON.stringify({ type }), '*')

export const initAttribution = (axios, apiRoot) => {
  // In an iframe the parent owns the URL and storage; hold only what it sends, in memory.
  const embedded = inIframe()
  let fromParent = null

  if (embedded) {
    window.addEventListener('message', e => {
      if (e.source !== window.parent) return
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
        if (data?.type !== 'knko-attr:set') return
        fromParent = typeof data.payload === 'object' ? data.payload : null
      } catch {
        // Ignore non-JSON messages
      }
    })
    tellParent('knko-attr:ready')
  } else {
    recordTouch(readTouch())
  }

  const isApiRequest = config => !!config?.url?.startsWith(apiRoot)

  axios.interceptors.request.use(config => {
    if (!isApiRequest(config)) return config
    const stored = embedded ? fromParent : current()
    if (stored) config.headers['X-Attribution'] = encode(stored)
    return config
  })

  // Success handler only, so a failed purchase keeps the tags for the retry.
  axios.interceptors.response.use(response => {
    if (
      isApiRequest(response.config) &&
      response.headers?.['x-attribution-consumed']
    ) {
      if (embedded) {
        fromParent = null
        tellParent('knko-attr:clear')
      } else {
        clear()
      }
    }
    return response
  })
}
